import React, { useRef, useState } from "react";
import {
  Button,
  Divider,
  Form,
  Icon,
  Message,
  Table,
} from "semantic-ui-react";
import { Student } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { withEmptyOption } from "../../../../utils";
import "./BulkImportStudentForm.scss";

const studentController = new Student();

const renderErrorMessage = (err) => {
  if (!err) return "";
  if (typeof err === "string") return err;
  if (err.message && typeof err.message === "string") return err.message;

  const { email, reason } = err;
  const formattedReason = typeof reason === "object" ? JSON.stringify(reason) : (reason || "");

  // If email is '[object Object]', it means Excel sent it as a hyperlink object
  if (email === "[object Object]") {
    return `Remueva el hipervínculo del correo en el Excel - ${formattedReason}`;
  }

  // If email exists, is not an object, and is not '[object Object]', display it with the reason
  if (email && typeof email !== "object") {
    return formattedReason ? `${email} - ${formattedReason}` : String(email);
  }

  // Otherwise, only display the reason
  return formattedReason || JSON.stringify(err);
};

export function BulkImportStudentForm(props) {
  const { onReload, academicCycle, gradesOptions } = props;
  const { accessToken } = useAuth();
  const fileInputRef = useRef(null);

  const [gradeId, setGradeId] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleDownloadTemplate = async () => {
    try {
      setDownloading(true);
      const blob = await studentController.downloadBulkTemplate(accessToken);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "plantilla-matricula.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError("No se pudo descargar la plantilla.");
    } finally {
      setDownloading(false);
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0] || null);
    setResult(null);
    setError(null);
  };

  const handleImport = async () => {
    if (!gradeId) { setError("Selecciona un grado."); return; }
    if (!file) { setError("Selecciona un archivo .xlsx."); return; }
    try {
      setLoading(true);
      setError(null);
      const data = await studentController.bulkImportStudents(
        accessToken,
        academicCycle.id,
        gradeId,
        file
      );
      setResult(data);
      if (data.successCount > 0) onReload();
    } catch (err) {
      setError(err?.message || "Error al importar el archivo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bulk-import-form">
      <Form>
        <div className="bulk-import-form__cards">
          <div className="bulk-import-form__card">
            <div className="bulk-import-form__card-icon bulk-import-form__card-icon--teal">
              <Icon name="file excel outline" />
            </div>
            <div className="bulk-import-form__card-body">
              <span className="bulk-import-form__card-title">Plantilla Excel</span>
              <span className="bulk-import-form__card-desc">
                Descarga el formato base y complétalo con los datos de los estudiantes.
              </span>
            </div>
            <Button
              type="button"
              basic
              color="teal"
              loading={downloading}
              onClick={handleDownloadTemplate}
            >
              <Icon name="download" /> Descargar plantilla
            </Button>
          </div>

          <div className="bulk-import-form__card">
            <div className="bulk-import-form__card-icon bulk-import-form__card-icon--primary">
              <Icon name="upload" />
            </div>
            <div className="bulk-import-form__card-body">
              <span className="bulk-import-form__card-title">Archivo a importar</span>
              <span className="bulk-import-form__card-desc">
                Selecciona el archivo .xlsx ya completado para iniciar la carga masiva.
              </span>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx"
              onChange={handleFileChange}
              className="bulk-import-form__file-input"
            />
            <Button
              type="button"
              basic
              onClick={() => fileInputRef.current.click()}
            >
              <Icon name="file excel" />
              {file ? file.name : "Seleccionar archivo"}
            </Button>
          </div>
        </div>

        <Divider className="bulk-import-form__separator" />

        <Form.Group className="bulk-import-form__grade-row" widths="equal">
          <Form.Select
            className="edunova-select"
            label="Grado"
            placeholder="Selecciona un grado"
            clearable
            options={withEmptyOption(gradesOptions, "Selecciona un grado")}
            value={gradeId}
            onChange={(_, { value }) => { setGradeId(value); setResult(null); setError(null); }}
          />
          <Form.Field className="bulk-import-form__import-field">
            <Button
              primary
              type="button"
              fluid
              loading={loading}
              disabled={loading || !gradeId || !file}
              onClick={handleImport}
            >
              <Icon name="upload" /> Importar
            </Button>
          </Form.Field>
        </Form.Group>

        {error && (
          <Message negative className="bulk-import-form__message">
            <Message.Header>Error</Message.Header>
            <p>{error}</p>
          </Message>
        )}

        {result && (
          <div className="bulk-import-form__result">
            <Divider className="bulk-import-form__separator" />

            <div className="bulk-import-form__summary">
              <div className="bulk-import-form__stat bulk-import-form__stat--total">
                <Icon name="list ol" />
                <span className="bulk-import-form__stat-value">{result.totalRows}</span>
                <span className="bulk-import-form__stat-label">Total filas</span>
              </div>
              <div className="bulk-import-form__stat bulk-import-form__stat--success">
                <Icon name="check circle" />
                <span className="bulk-import-form__stat-value">{result.successCount}</span>
                <span className="bulk-import-form__stat-label">Exitosos</span>
              </div>
              <div
                className={`bulk-import-form__stat ${result.errorCount > 0
                  ? "bulk-import-form__stat--error"
                  : "bulk-import-form__stat--total"
                  }`}
              >
                <Icon name="exclamation circle" />
                <span className="bulk-import-form__stat-value">{result.errorCount}</span>
                <span className="bulk-import-form__stat-label">Con error</span>
              </div>
            </div>

            {result.errorCount === 0 && (
              <Message positive className="bulk-import-form__message">
                <Icon name="check circle" />
                Todos los registros fueron importados correctamente.
              </Message>
            )}

            {result.errors && result.errors.length > 0 && (
              <div className="bulk-import-form__table-wrapper">
                <Table compact size="small" celled>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>Fila</Table.HeaderCell>
                      <Table.HeaderCell>Error</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {result.errors.map((err, i) => (
                      <Table.Row key={i} negative>
                        <Table.Cell>{err.row ?? i + 1}</Table.Cell>
                        <Table.Cell>{renderErrorMessage(err)}</Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </div>
            )}
          </div>
        )}
      </Form>
    </div>
  );
}
